import { useCallback, useEffect, useState } from 'react';

// Browsers clamp longer delays to a signed 32-bit integer. Re-arm long retention
// windows rather than scheduling ninety days as one overflowing timeout.
const MAX_TIMER_DELAY = 2_147_483_647;

/** Keep an open recording player in sync with its retention deadline. */
export function useRecordingAvailability(recordingAvailable: boolean, expiresAt?: string) {
  const deadline = expiresAt ? Date.parse(expiresAt) : Number.NaN;
  const [checkedAt, setCheckedAt] = useState(Date.now);
  const isAvailable = useCallback(() => recordingAvailable && Number.isFinite(deadline) && Date.now() < deadline, [recordingAvailable, deadline]);
  const recheck = useCallback(() => setCheckedAt(Date.now()), []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      if (timer !== undefined) clearTimeout(timer);
      const now = Date.now();
      setCheckedAt(now);
      if (recordingAvailable && Number.isFinite(deadline) && now < deadline) timer = setTimeout(check, Math.min(deadline - now, MAX_TIMER_DELAY));
    };
    check();
    // Suspended/background tabs may delay timers; check again as they resume.
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => { if (timer !== undefined) clearTimeout(timer); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check); };
  }, [recordingAvailable, deadline]);

  return { available: recordingAvailable && checkedAt < deadline && isAvailable(), isAvailable, recheck };
}
